package one.healthium.ui.compose.components

import androidx.compose.animation.core.AnimationSpec
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.composables.core.ModalBottomSheet
import com.composables.core.ModalBottomSheetState
import com.composables.core.Scrim
import com.composables.core.Sheet
import com.composables.core.SheetDetent
import com.composables.core.SheetDetent.Companion.FullyExpanded
import com.composables.core.SheetDetent.Companion.Hidden
import com.composables.core.rememberModalBottomSheetState
import io.github.vinceglb.confettikit.compose.ConfettiKit
import io.github.vinceglb.confettikit.core.Party
import io.github.vinceglb.confettikit.core.Position
import io.github.vinceglb.confettikit.core.emitter.Emitter
import one.healthium.ui.compose.back_handler.AppBackPressHandler
import one.healthium.ui.compose.back_handler.LocalBackPressHandler
import one.healthium.ui.compose.components.adaptive.AppAdaptiveDefaults
import one.healthium.ui.compose.theme.AppTheme
import kotlin.time.Duration.Companion.milliseconds

private val enterTransition = fadeIn(animationSpec = tween(durationMillis = 150))
private val exitTransition = fadeOut(animationSpec = tween(durationMillis = 150))

@Composable
internal expect fun MakeStyleSystemBars()

@Composable
fun rememberAppModalBottomSheetState(
    initialDetent: SheetDetent,
    detents: List<SheetDetent> = listOf(Hidden, FullyExpanded),
    animationSpec: AnimationSpec<Float> = tween(),
    velocityThreshold: () -> Dp = { 205.dp },
    positionalThreshold: (totalDistance: Dp) -> Dp = { it * 0.7f },
    confirmDetentChange: (SheetDetent) -> Boolean = { true },
): ModalBottomSheetState {
    return rememberModalBottomSheetState(
        initialDetent = initialDetent,
        detents = detents,
        animationSpec = animationSpec,
        velocityThreshold = velocityThreshold,
        positionalThreshold = positionalThreshold,
        confirmDetentChange = confirmDetentChange,
    )
}


@Composable
fun AppModalBottomSheet(
    state: ModalBottomSheetState,
    onDismissed: () -> Unit = {},
    showConfetti: Boolean = false,
    sheetPadding: PaddingValues = PaddingValues(),
    content: @Composable ColumnScope.() -> Unit,
) {
    val backPressHandler = remember(state) {
        AppBackPressHandler { state.targetDetent = Hidden }
    }

    // https://github.com/composablehorizons/compose-unstyled/issues/202
    var hasBeenShown by remember { mutableStateOf(false) }
    val isSheetVisible by remember { derivedStateOf { state.offset > 0 || state.targetDetent != Hidden } }

    LaunchedEffect(hasBeenShown, isSheetVisible) {
        if (hasBeenShown && isSheetVisible.not()) {
            onDismissed()
        }
    }

    ModalBottomSheet(
        state = state,
//        onDismiss = onDismissed,
    ) {
        Scrim(
            enter = enterTransition,
            exit = exitTransition,
        )
        Sheet(
            modifier = Modifier
                .statusBarsPadding()
                .imePadding()
                .padding(sheetPadding)
                .shadow(4.dp, AppTheme.shapes.sheet)
                .clip(AppTheme.shapes.sheet)
                .background(Color.White)
                .widthIn(max = AppAdaptiveDefaults.maxWidthMediumMode)
                .heightIn(max = AppAdaptiveDefaults.maxHeightMediumMode)
                .fillMaxWidth()
        ) {
            DisposableEffect(Unit) {
                hasBeenShown = true
                onDispose {
                    hasBeenShown = false
                }
            }
            MakeStyleSystemBars()
            CompositionLocalProvider(LocalBackPressHandler provides backPressHandler) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    content()
                }
            }
        }

        if (showConfetti) {
            ConfettiKit(
                modifier = Modifier.fillMaxSize(),
                parties = listOf(
                    Party(
                        speed = 0f,
                        maxSpeed = 30f,
                        damping = 0.9f,
                        spread = 360,
                        position = Position.Relative(0.5, 0.2),
                        colors = listOf(0xfce18a, 0xff726d, 0xf4306d, 0xb48def),
                        emitter = Emitter(duration = 500.milliseconds).max(300),
                    )
                )
            )
        }
    }
}